import { unwrapResult } from '@reduxjs/toolkit';
import { Modal } from 'antd';
import {
  editChangeRequestItem,
  getChangeRequestResult,
  implementChangeRequestTask,
} from '../app/ActionTypes/changeRequestActionTypes';
import store, { AppDispatch } from '../app/store';
import {
  ChecklistItem,
  ExecutionItem,
  Result,
} from '../interfaces/changeRequest';
import {
  ChangeRequestPermission,
  ChangeRequestSource,
} from '../enums/changeRequest';
import { refreshCredentials, verifyCredential } from './organizationService';
import { getAppLoaderController } from '../hooks/loadingContext';
import { Notify } from './notificationService';
import { OrgCredential } from '../interfaces/organization';
import { fetchChangeRequests } from '../app/ActionTypes/changeRequestsActions';
import { CirraServiceType } from '../enums/auth';
import { ExponentialBackoff } from '../utils/exponentialBackoff';
import { setRevokeAPICalls as setRevokeAPICallsAction } from '../app/Slices/changeRequestStateSlice';

export const IMPLEMENTABLE_STATUSES = ['pending', 'error', 'input needed'];

export const getChangeRequestTitle = (changeRequest: Result) => {
  return [changeRequest.key, changeRequest.name].filter(Boolean).join(': ');
};
export const changeRequestResultPolling = async (
  dispatch: AppDispatch,
  operationId: string,
  source: ChangeRequestSource,
  initialInterval = 1000
): Promise<boolean> => {
  // Use exponential backoff for polling
  const backoff = new ExponentialBackoff({
    initialDelay: initialInterval,
    maxDelay: 10000, // Max 10 seconds between polls
    multiplier: 1.5,
    maxRetries: 60, // Max ~5 minutes of polling
    jitter: true,
  });

  try {
    while (backoff.canRetry()) {
      if (store.getState().agent.changeRequestState.revokeAPICalls) {
        return false;
      }

      try {
        const resultResponse = await dispatch(
          getChangeRequestResult({ croId: operationId, source })
        );
        const response = unwrapResult(resultResponse);

        if (response?.data?.status === 'completed') {
          return true;
        } else if (response?.data?.status === 'failed') {
          const editingItem =
            store.getState().agent.changeRequestEdit.editingItem;
          response.data.error &&
            !editingItem &&
            Notify.error(response.data.error, 0);
          return false;
        }

        // Status is still pending/in-progress, wait with backoff
        const delay = backoff.next();
        await new Promise((resolve) => setTimeout(resolve, delay));
      } catch (error) {
        // Network or API error - use backoff before retry
        if (!backoff.canRetry()) {
          throw error;
        }
        const delay = backoff.next();
        await new Promise((resolve) => setTimeout(resolve, delay));
      }
    }

    // Max retries reached
    Notify.error('Operation timed out. Please check the status manually.');
    return false;
  } catch (error) {
    Notify.error('Polling failed. Please try again later.');
    return false;
  }
};

export let currentActionItemId: string | null = null;

export const implementAllActionItems = async (
  dataResult: Result,
  dispatch: AppDispatch
): Promise<void> => {
  try {
    const isUserConfirmed = await confirmSFOrgUser();

    if (!isUserConfirmed) {
      return;
    }

    // Proceed with processing tasks
    const { id, execution } = dataResult;
    dispatch(setRevokeAPICallsAction(false));
    let implementedCount = 0;
    // Process items sequentially to maintain order and prevent race conditions
    for (let index = 0; index < execution.items.length; index++) {
      // Verifying if the implementation has been halted by another operation.
      if (store.getState().agent.changeRequestState.revokeAPICalls) {
        break;
      }
      const item = execution.items[index];
      try {
        if (
          item.type === 'automated task' &&
          IMPLEMENTABLE_STATUSES.includes(item.status)
        ) {
          // Get the latest state data for each iteration
          const currentState = store.getState().agent.changeRequest.data;

          if (currentState?.result) {
            await handleImplementRow(
              dispatch,
              currentState.result,
              index,
              id,
              item,
              ChangeRequestSource.ACTIONS_IMPLEMENT_ALL
            );
            implementedCount++;
          }
        }
      } catch (itemError) {
        break;
      }
    }
    if (!store.getState().agent.changeRequestState.revokeAPICalls) {
      handleImplementAllNotify(implementedCount);
    }
  } catch (error) {
    console.error(`Error while implementinh all tasks`, error);
    Notify.error('Failed to implement any tasks!');
  }
};

export const saveRecord = async (
  dispatch: AppDispatch,
  index: number,
  updatedItem: ExecutionItem | ChecklistItem,
  sourceEdit: ChangeRequestSource,
  sourcePath = '/execution'
) => {
  const crId = store.getState().agent.changeRequestState.changeRequestId;
  if (!crId) {
    Notify.error('No change request selected');
    return null;
  }

  try {
    const response = await dispatch(
      editChangeRequestItem({
        path: `${sourcePath}/items/${index}`,
        updatedItem,
        crId,
        source: sourceEdit,
      })
    );
    await dispatch(fetchChangeRequests());
    const payload = response.payload as { error?: boolean };
    if (payload.error) {
      Notify.error(
        `Field ${updatedItem.item} either doesn't exist or is not updatable`
      );
    }
    const payloadResult = response.payload as {
      data: Result;
    };
    return payloadResult.data;
  } catch (error) {
    Notify.error('Error editing item');
  }
};

const handleImplementAllNotify = (implementedCount: number) => {
  const updatedDataResult = store.getState().agent.changeRequest.data?.result;
  if (!updatedDataResult) {
    return;
  }
  const completedItems = updatedDataResult.execution.items.filter(
    (item) => item.type === 'automated task' && item.status === 'completed'
  );
  if (implementedCount === 0) {
    Notify.info('No automated tasks with PENDING or ERROR status to process.');
  } else if (implementedCount === completedItems.length) {
    Notify.success('All automated tasks have been successfully completed!');
  } else if (implementedCount > 0 && completedItems.length > 0) {
    Notify.info(
      `${completedItems.length} automated task(s) completed successfully.`
    );
  }
};

export const handleImplementRow = async (
  dispatch: AppDispatch,
  dataResult: Result,
  index: number,
  crId: string,
  record: ExecutionItem,
  source: ChangeRequestSource
): Promise<boolean> => {
  if (store.getState().agent.changeRequestState.revokeAPICalls) {
    return false;
  }
  let updatedRecord = dataResult;
  const currentRow = { ...record };
  currentActionItemId = record.id;
  if (record.status === 'error' || record.status === 'input needed') {
    currentRow.status = 'pending';
    updatedRecord = (await saveRecord(
      dispatch,
      index,
      currentRow,
      source
    )) as Result;
  }

  try {
    const response = await dispatch(
      implementChangeRequestTask({
        id: crId,
        taskId: String(index), // Current iteration loop ID
        bodyData: updatedRecord,
        source,
      })
    );
    await dispatch(fetchChangeRequests());
    const {
      data: { id: newOperationId },
    } = unwrapResult(response);

    if (!newOperationId) {
      return false;
    }

    return await changeRequestResultPolling(dispatch, newOperationId, source);
  } catch (error) {
    console.error('Error during action item implementation', error);
    return false;
  }
};

const confirmSFOrgUser = async () => {
  const { setAppLoading } = getAppLoaderController();

  try {
    setAppLoading(true);
    const selectedCredential = store.getState().organization.org;
    if (!selectedCredential) {
      Notify.warning('No organization selected!');
      return false;
    }
    const isActiveOrgUser = await verifyCredential(selectedCredential);

    if (!isActiveOrgUser) {
      // Prompt the user for re-authentication
      const userConfirmed = await showCustomConfirm();

      if (userConfirmed) {
        try {
          const refreshResult = await refreshCredentials(
            selectedCredential,
            CirraServiceType.AGENTS
          );

          if (refreshResult.success) {
            Notify.success('Authentication successful! Proceeding with tasks.');
            return true;
          } else {
            Notify.error('Authentication failed! Cannot proceed.');
            return false; // Exit if re-authentication fails
          }
        } catch (authError) {
          Notify.error(`Authentication error: ${authError}`);
          return false; // Exit if an error occurs during authentication
        }
      } else {
        Notify.info('Action cancelled by the user.');
        return false; // Exit if the user cancels
      }
    }
    return true;
  } catch (error) {
    Notify.error('Failed to verify user credentials!');
    return false;
  } finally {
    setAppLoading(false);
  }
};

const showCustomConfirm = (): Promise<boolean> => {
  return new Promise((resolve) => {
    Modal.confirm({
      title: 'Re-authentication Required',
      content: 'Your selected SF Org user is not authorized with Cirra AI API.',
      okText: 'Authenticate',
      cancelText: 'Cancel',
      onOk: () => resolve(true), // Resolves the promise if "Authenticate" is clicked
      onCancel: () => resolve(false), // Resolves the promise if "Cancel" is clicked
    });
  });
};

export const getChangeRequestPermissions = (
  org: OrgCredential | null,
  cr: Result
) => {
  if (!org?.cirra_user_id || !org?.user_id) {
    return [];
  }
  const sameCirraOrg = cr.cirra_organization_id === org.cirra_organization_id;
  const sameCirraUser = cr.cirra_user_id === org.cirra_user_id;
  const sameSfOrg = cr.sf_organization_id === org.organization_id;
  const sameSfUser = cr.sf_user_id === org.user_id;

  // CR of personal user
  if (!org.cirra_organization_id && sameCirraUser && sameSfUser) {
    return [
      ChangeRequestPermission.VIEW,
      ChangeRequestPermission.EDIT,
      ChangeRequestPermission.DELETE,
    ];
  }

  // CR of organization user
  if (sameCirraOrg && sameCirraUser && sameSfUser) {
    return [
      ChangeRequestPermission.VIEW,
      ChangeRequestPermission.EDIT,
      ChangeRequestPermission.DELETE,
    ];
  }
  if (sameCirraOrg || sameSfOrg) {
    return [ChangeRequestPermission.VIEW];
  }
  return [];
};

export const isOwnChangeRequest = (org: OrgCredential | null, cr: Result) => {
  return org?.cirra_user_id === cr.cirra_user_id;
};
